import React, { useMemo, useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { faArrowDown, faArrowUp, faClose, faGripVertical, faLayerGroup } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import { CategoryEntity } from "../../../Objects/Category/CategoryEntity"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { PutFormRequest } from "../../../services/ApiClientService"
import { useError } from "../../../context/useErrorContext"
import { useLoad } from "../../../context/useLoadContext"
import GetSpace from "../../../utils/space"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"

interface Props {
  categories: CategoryEntity[]
  onClose: () => void
  onSaved: () => void
}

export const CategoryOrderPopup: React.FC<Props> = ({ categories, onClose, onSaved }) => {
  const { t } = useTranslation("category")

  const { setError } = useError()

  const { enableLoad, disableLoad } = useLoad()

  const [orderedCategories, setOrderedCategories] = useState<CategoryEntity[]>(
    [...categories].sort(
      (a, b) => (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER),
    ),
  )
  const [draggedId, setDraggedId] = useState<number | null>(null)

  const hasManualOrder = useMemo(() => orderedCategories.some((c) => c.displayOrder !== null), [orderedCategories])

  const moveCategory = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= orderedCategories.length || fromIndex === toIndex) return

    const updated = [...orderedCategories]

    const [moved] = updated.splice(fromIndex, 1)

    updated.splice(toIndex, 0, moved)

    setOrderedCategories(updated)
  }

  const handleSaveOrder = async () => {
    enableLoad()

    try {
      await Promise.all(
        orderedCategories.map((category, index) => {
          const updated = new CategoryEntity({
            ...category,
            displayOrder: index + 1,
          })

          return PutFormRequest(`${API_ROUTES.CATEGORY.PUT}/${category.id}`, updated.toJSON())
        }),
      )

      onSaved()
      onClose()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("general:label_unknown_error_description")
      setError(t("general:label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handleClearManualOrder = async () => {
    enableLoad()

    try {
      await Promise.all(
        orderedCategories.map((category) => {
          const updated = new CategoryEntity({
            ...category,
            displayOrder: null,
          })

          return PutFormRequest(`${API_ROUTES.CATEGORY.PUT}/${category.id}`, updated.toJSON())
        }),
      )

      onSaved()
      onClose()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("general:label_unknown_error_description")
      setError(t("general:label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert} max-w-[860px] w-[95%]`}>
            <TitlePageResponsiveIconSmall
              title={t("label_order_popup_title")}
              icon={faLayerGroup}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <PopupHelpButton
                    helpKey="pages.products.category.categoryorderpopup.1"
                    variant="global"
                    title={t("helpPopup:global.categoryOrder.title")}
                    description={t("helpPopup:global.categoryOrder.description")}
                    bullets={t("helpPopup:global.categoryOrder.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.products.category.categoryOrderPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
              <div className="rounded-lg border-2 border-orange-100  px-3 py-2 text-[12px] text-orange-700">
                {t("label_order_popup_hint")}
              </div>
            </Animate>

            <GetSpace top={10} />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2">
                {orderedCategories.map((category, index) => (
                  <Animate key={category.id} type={AnimationTypes.fadeInTraslate} duration={1.5 + index * 0.4 + "s"}>
                    <div
                      draggable
                      onDragStart={() => setDraggedId(category.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (draggedId === null) return

                        const fromIndex = orderedCategories.findIndex((item) => item.id === draggedId)
                        moveCategory(fromIndex, index)
                        setDraggedId(null)
                      }}
                      className="mb-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm transition hover:border-orange-200 hover:shadow"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 cursor-move rounded-lg bg-gray-100 px-2 py-1">
                            <FontAwesomeIcon icon={faGripVertical} />
                          </span>

                          <span className="min-w-8 rounded-lg bg-orange-100 px-2 py-1 text-center text-xs font-bold text-orange-700">
                            {index + 1}
                          </span>

                          <span className="text-xl">{category.icon}</span>

                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800">{category.name}</span>

                            <span className="text-xs text-gray-500">
                              {t("label_order_position", { value: index + 1 })}
                            </span>
                          </div>
                        </div>

                        <CanView permission={Permissions.CanEditeCategories}>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="rounded-lg border border-gray-200 px-2 py-1 text-sm hover:bg-gray-100"
                              onClick={() => moveCategory(index, index - 1)}
                              data-interaction-description-key="interactionDescriptions:pages.products.category.categoryOrderPopup.button.moveCategoryIndexIndex1"
                            >
                              <FontAwesomeIcon icon={faArrowUp} />
                            </button>

                            <button
                              type="button"
                              className="rounded-lg border border-gray-200 px-2 py-1 text-sm hover:bg-gray-100"
                              onClick={() => moveCategory(index, index + 1)}
                              data-interaction-description-key="interactionDescriptions:pages.products.category.categoryOrderPopup.button.moveCategoryIndexIndex12"
                            >
                              <FontAwesomeIcon icon={faArrowDown} />
                            </button>
                          </div>
                        </CanView>
                      </div>
                    </div>
                  </Animate>
                ))}
              </div>
            </Animate>

            <GetSpace top={10} />

            <CanView permission={Permissions.CanEditeCategories}>
              <div className={`${defaultStyle.buttonsController} border-t border-gray-100 pt-3`}>
                <Button
                  label={t("buttons:button_save")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  onClick={handleSaveOrder}
                  descriptionKey="interactionDescriptions:pages.products.category.categoryOrderPopup.button.save"
                />

                <GetSpace right={5} />

                {hasManualOrder && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("label_clear_manual_order")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRemoveColor}
                      onClick={handleClearManualOrder}
                      descriptionKey="interactionDescriptions:pages.products.category.categoryOrderPopup.button.clearManualOrder"
                    />
                  </>
                )}
              </div>
            </CanView>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
